import {
  Box,
  Button,
  ButtonProps,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { abyssColors } from '@/theme/colors';
import { useEffect, useState } from 'react';
import { AllStructuresForHomePageQuery } from '@/__generated__/graphql';
import { useAtom } from 'jotai';
import * as kpiState from '../../state';
import { usePersistKpiFilters } from '../../hooks/usePersistKpiFilters';

type Props = {
  deployments: AllStructuresForHomePageQuery['allStructures'];
} & ButtonProps;

export const KpiStructureConfigureButton = ({ deployments: allDeployments, ...rest }: Props) => {
  const [selectedDeploymentIds, setSelectedDeploymentIds] = useAtom(kpiState.selectedDeploymentIds);
  const [open, setOpen] = useState(false);
  const [deploymentIds, setDeploymentIds] = useState<string[]>([]);
  const { loading: persistingFilters, persistSelectedDeploymentsFilter } = usePersistKpiFilters();

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setSelectedDeploymentIds((prev) => (prev?.length ? prev : allDeployments.map((d) => d.id)));
  }, [allDeployments, setSelectedDeploymentIds]);

  useEffect(() => {
    setDeploymentIds(selectedDeploymentIds);
  }, [selectedDeploymentIds]);

  const onApply = () => {
    persistSelectedDeploymentsFilter(deploymentIds)
      .then(() => {
        setSelectedDeploymentIds(deploymentIds);
        onClose();
      })
      .catch();
  };

  return (
    <>
      <Button
        id="kpi-tracker-deployment-configure-button"
        variant="outlined"
        size="small"
        startIcon={<SettingsIcon />}
        {...rest}
        onClick={() => setOpen(true)}
      >
        Configure
      </Button>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            padding: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.8rem',
            fontWeight: 600,
            color: abyssColors.primary[700],
            padding: 0,
            mb: 3,
          }}
        >
          Configuration
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          <Typography variant="body2" color={abyssColors.primary[600]} mb={2}>
            Select deployments to include in risk analysis:
          </Typography>

          <Stack spacing={1}>
            {allDeployments.length === 0 ? (
              <Typography color={abyssColors.primary[500]} fontStyle="italic">
                No deployments available
              </Typography>
            ) : (
              allDeployments.map((item) => {
                const checked = deploymentIds.includes(item.id);
                return (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          disabled={checked && deploymentIds.length === 1}
                          onChange={(event) => {
                            const next = event.target.checked;
                            if (next) {
                              setDeploymentIds((prev) => [...prev, item.id]);
                            } else {
                              setDeploymentIds((prev) => prev.filter((id) => id !== item.id));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color={abyssColors.primary[700]}>
                          {item.name}
                        </Typography>
                      }
                    />
                  </Box>
                );
              })
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined" disabled={persistingFilters}>
            Cancel
          </Button>
          <Button onClick={onApply} variant="contained" disabled={persistingFilters}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
